import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Download, FileJson, Loader2, RefreshCw } from 'lucide-react';
import { auth } from '../firebase';
import { fetchAdminDashboardUsage, fetchAdminDashboardUsageExport } from '../utils/adminModelsApi';
import AdminLayout from '../components/admin/AdminLayout';

const MODEL_COLORS = [
  '#2563eb',
  '#0891b2',
  '#16a34a',
  '#ca8a04',
  '#dc2626',
  '#9333ea',
  '#db2777',
  '#0d9488',
  '#ea580c',
  '#4f46e5',
];
const OTHER_COLOR = '#94a3b8';
const STACK_MAX_MODELS = 8;
const USAGE_METRIC_CONFIG = {
  tokens: {
    label: 'Tokens',
    unitPlural: 'tokens',
    totalLabel: 'Total Tokens',
    filteredTotalLabel: 'Tokens (filtered)',
    dailyLabel: 'Daily Token Usage',
    filteredDailyLabel: 'Daily Token Usage (filtered)',
  },
  messages: {
    label: 'Messages',
    unitPlural: 'messages',
    totalLabel: 'Total Messages',
    filteredTotalLabel: 'Messages (filtered)',
    dailyLabel: 'Daily Message Usage',
    filteredDailyLabel: 'Daily Message Usage (filtered)',
  },
};

const getUsageMetricConfig = (metric) =>
  USAGE_METRIC_CONFIG[metric] || USAGE_METRIC_CONFIG.tokens;

const EXPORT_STATUS_TIMEOUT_MS = 3500;

const getDateKeyFromDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const shiftDate = (date, daysDelta) => {
  const next = new Date(date);
  next.setDate(next.getDate() + daysDelta);
  return next;
};

const getDefaultDateRange = () => {
  const today = new Date();
  return {
    endDate: getDateKeyFromDate(today),
    startDate: getDateKeyFromDate(shiftDate(today, -29)),
  };
};

const formatDateKey = (dateKey) => {
  const date = new Date(`${dateKey}T00:00:00`);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(value || 0);

const pluralize = (count, singular, plural) =>
  count === 1 ? `${count} ${singular}` : `${formatNumber(count)} ${plural}`;

const sanitizeFilePart = (value) =>
  String(value || '')
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

const buildExportFilename = ({ startDate, endDate, userId, format }) => {
  const filterPart = userId ? `-${sanitizeFilePart(userId)}` : '';
  return `arrowai-token-usage-${startDate}-to-${endDate}${filterPart}.${format}`;
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const polarToCartesian = (cx, cy, r, angleDeg) => {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(angleRad), y: cy + r * Math.sin(angleRad) };
};

const describeArc = (cx, cy, innerR, outerR, startAngle, endAngle) => {
  const start = polarToCartesian(cx, cy, outerR, endAngle);
  const end = polarToCartesian(cx, cy, outerR, startAngle);
  const innerStart = polarToCartesian(cx, cy, innerR, endAngle);
  const innerEnd = polarToCartesian(cx, cy, innerR, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? '0' : '1';
  return [
    'M', start.x, start.y,
    'A', outerR, outerR, 0, largeArc, 0, end.x, end.y,
    'L', innerEnd.x, innerEnd.y,
    'A', innerR, innerR, 0, largeArc, 1, innerStart.x, innerStart.y,
    'Z',
  ].join(' ');
};

const StatCard = ({ label, value, hint }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
    <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</p>
    <p className="mt-2 text-2xl font-semibold text-gray-900">{formatNumber(value)}</p>
    {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
  </div>
);

// ---------- charts ----------

const ChartTooltip = ({ x, y, visible, children }) => {
  if (!visible) return null;
  return (
    <div
      className="pointer-events-none absolute z-20 rounded-md bg-gray-900 text-white text-xs px-2.5 py-1.5 shadow-lg whitespace-nowrap"
      style={{
        left: `${x}px`,
        top: `${y}px`,
        transform: 'translate(-50%, calc(-100% - 10px))',
      }}
    >
      {children}
    </div>
  );
};

const Legend = ({ items }) => (
  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
    {items.map((item) => (
      <div key={item.label} className="flex items-center gap-1.5 text-xs text-gray-600">
        <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: item.color }} />
        <span className="truncate max-w-[180px]">{item.label}</span>
      </div>
    ))}
  </div>
);

const DailyUsageChart = ({ daily, label = 'Daily Usage', unitLabel = 'tokens' }) => {
  const containerRef = useRef(null);
  const [hover, setHover] = useState(null);

  if (!daily.length || daily.every((d) => (d.total || 0) === 0)) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">{label}</h2>
        <p className="mt-3 text-sm text-gray-500">No usage data for this selection.</p>
      </div>
    );
  }

  const width = 900;
  const height = 260;
  const paddingLeft = 44;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 36;
  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;
  const maxTotal = Math.max(...daily.map((point) => point.total), 1);
  const xStep = daily.length > 1 ? chartWidth / (daily.length - 1) : chartWidth;

  const points = daily.map((point, index) => {
    const x = paddingLeft + index * xStep;
    const y = paddingTop + chartHeight - (point.total / maxTotal) * chartHeight;
    return { ...point, x, y };
  });

  const polylinePoints = points.map((point) => `${point.x},${point.y}`).join(' ');
  const areaPoints = `${paddingLeft},${paddingTop + chartHeight} ${polylinePoints} ${paddingLeft + (daily.length - 1) * xStep},${paddingTop + chartHeight}`;
  const yTicks = 4;

  const handleMove = (event) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.width === 0) return;
    const relX = event.clientX - rect.left;
    const vbX = (relX / rect.width) * width;
    const idx = Math.max(
      0,
      Math.min(points.length - 1, Math.round((vbX - paddingLeft) / xStep))
    );
    const pt = points[idx];
    const pxX = (pt.x / width) * rect.width;
    const pxY = (pt.y / height) * rect.height;
    setHover({ idx, pxX, pxY, point: pt });
  };

  const hoveredPoint = hover?.point;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">{label}</h2>
        <span className="text-xs text-gray-500">{pluralize(daily.length, 'day', 'days')}</span>
      </div>
      <div
        ref={containerRef}
        className="relative mt-3 overflow-x-auto"
        onMouseMove={handleMove}
        onMouseLeave={() => setHover(null)}
      >
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full min-w-[640px] block">
          <defs>
            <linearGradient id="lineFill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#2563eb" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
            </linearGradient>
          </defs>
          {Array.from({ length: yTicks + 1 }).map((_, index) => {
            const value = Math.round((maxTotal / yTicks) * index);
            const y = paddingTop + chartHeight - (index / yTicks) * chartHeight;
            return (
              <g key={index}>
                <line x1={paddingLeft} x2={width - paddingRight} y1={y} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                <text x={paddingLeft - 6} y={y + 4} textAnchor="end" fontSize="10" fill="#6b7280">
                  {formatNumber(value)}
                </text>
              </g>
            );
          })}

          <polygon points={areaPoints} fill="url(#lineFill)" />
          <polyline
            points={polylinePoints}
            fill="none"
            stroke="#2563eb"
            strokeWidth="2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {hoveredPoint && (
            <g>
              <line
                x1={hoveredPoint.x}
                x2={hoveredPoint.x}
                y1={paddingTop}
                y2={paddingTop + chartHeight}
                stroke="#9ca3af"
                strokeWidth="1"
                strokeDasharray="3 3"
              />
              <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r="6" fill="#2563eb" fillOpacity="0.18" />
              <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r="4" fill="#2563eb" stroke="#fff" strokeWidth="1.5" />
            </g>
          )}

          {points.map((point) => (
            <circle key={point.dateKey} cx={point.x} cy={point.y} r="2.5" fill="#2563eb" />
          ))}

          {points.map((point, index) => {
            const showLabel =
              index === 0 ||
              index === points.length - 1 ||
              index === Math.floor(points.length / 2);
            if (!showLabel) return null;
            return (
              <text
                key={`label-${point.dateKey}`}
                x={point.x}
                y={height - 12}
                textAnchor="middle"
                fontSize="10"
                fill="#6b7280"
              >
                {formatDateKey(point.dateKey)}
              </text>
            );
          })}
        </svg>

        <ChartTooltip x={hover?.pxX || 0} y={hover?.pxY || 0} visible={!!hover}>
          {hoveredPoint && (
            <div className="space-y-0.5">
              <div className="text-[10px] uppercase tracking-wider text-gray-400">
                {formatDateKey(hoveredPoint.dateKey)}
              </div>
              <div className="font-semibold">
                {formatNumber(hoveredPoint.total)} {unitLabel}
              </div>
            </div>
          )}
        </ChartTooltip>
      </div>
    </div>
  );
};

const StackedBarChart = ({ daily, models, unitLabel = 'tokens' }) => {
  const containerRef = useRef(null);
  const [hover, setHover] = useState(null);

  const modelOrder = models.slice(0, STACK_MAX_MODELS);
  const modelNames = modelOrder.map((m) => m.model);
  const hasOther = models.length > STACK_MAX_MODELS;
  const colorByModel = new Map(modelNames.map((name, i) => [name, MODEL_COLORS[i % MODEL_COLORS.length]]));

  const filledDaily = daily.map((day) => {
    const byModel = new Map((day.models || []).map((m) => [m.model, m.count]));
    const segments = modelNames.map((name) => ({ name, count: byModel.get(name) || 0 }));
    const tracked = segments.reduce((sum, s) => sum + s.count, 0);
    const other = Math.max(0, (day.total || 0) - tracked);
    if (hasOther && other > 0) segments.push({ name: 'Other', count: other });
    return { dateKey: day.dateKey, segments, total: day.total || 0 };
  });

  const maxTotal = Math.max(...filledDaily.map((d) => d.total), 1);
  const width = 900;
  const height = 260;
  const paddingLeft = 44;
  const paddingRight = 20;
  const paddingTop = 16;
  const paddingBottom = 36;
  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;
  const slotWidth = chartWidth / Math.max(filledDaily.length, 1);
  const barWidth = Math.max(2, Math.min(slotWidth * 0.7, 18));
  const yTicks = 4;

  const legendItems = [
    ...modelNames.map((name) => ({ label: name, color: colorByModel.get(name) })),
    ...(hasOther ? [{ label: 'Other', color: OTHER_COLOR }] : []),
  ];

  if (!daily.length || filledDaily.every((d) => d.total === 0)) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">Daily Usage by Model</h2>
        <p className="mt-3 text-sm text-gray-500">No usage data for this selection.</p>
      </div>
    );
  }

  const toPixel = (vbX, vbY) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return { x: 0, y: 0 };
    return { x: (vbX / width) * rect.width, y: (vbY / height) * rect.height };
  };

  const handleSegmentEnter = (day, seg, vbX, vbY) => {
    const { x, y } = toPixel(vbX, vbY);
    setHover({ day, seg, x, y });
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Daily Usage by Model</h2>
        <span className="text-xs text-gray-500">top {modelNames.length} models</span>
      </div>
      <div
        ref={containerRef}
        className="relative mt-3 overflow-x-auto"
        onMouseLeave={() => setHover(null)}
      >
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full min-w-[640px] block">
          {Array.from({ length: yTicks + 1 }).map((_, index) => {
            const value = Math.round((maxTotal / yTicks) * index);
            const y = paddingTop + chartHeight - (index / yTicks) * chartHeight;
            return (
              <g key={index}>
                <line x1={paddingLeft} x2={width - paddingRight} y1={y} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                <text x={paddingLeft - 6} y={y + 4} textAnchor="end" fontSize="10" fill="#6b7280">
                  {formatNumber(value)}
                </text>
              </g>
            );
          })}

          {filledDaily.map((day, index) => {
            const xCenter = paddingLeft + slotWidth * index + slotWidth / 2;
            const hoveredThisDay = hover?.day.dateKey === day.dateKey;
            let cursorY = paddingTop + chartHeight;
            return (
              <g key={day.dateKey}>
                {day.segments.map((seg) => {
                  if (seg.count <= 0) return null;
                  const segHeight = (seg.count / maxTotal) * chartHeight;
                  const y = cursorY - segHeight;
                  cursorY = y;
                  const fill = seg.name === 'Other' ? OTHER_COLOR : colorByModel.get(seg.name);
                  const isHovered = hover && hover.day.dateKey === day.dateKey && hover.seg.name === seg.name;
                  const isDimmed = hover && !isHovered;
                  return (
                    <rect
                      key={`${day.dateKey}-${seg.name}`}
                      x={xCenter - barWidth / 2}
                      y={y}
                      width={barWidth}
                      height={segHeight}
                      fill={fill}
                      fillOpacity={isDimmed ? 0.35 : 1}
                      stroke={isHovered ? '#111827' : 'none'}
                      strokeWidth={isHovered ? 1 : 0}
                      rx={1}
                      onMouseEnter={() => handleSegmentEnter(day, seg, xCenter, y)}
                      onMouseMove={() => handleSegmentEnter(day, seg, xCenter, y)}
                      style={{ cursor: 'pointer' }}
                    />
                  );
                })}
                {hoveredThisDay && (
                  <text
                    x={xCenter}
                    y={paddingTop + chartHeight + 14}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#111827"
                    fontWeight="600"
                  >
                    {formatDateKey(day.dateKey)}
                  </text>
                )}
                {!hoveredThisDay && (index === 0 || index === filledDaily.length - 1 || index === Math.floor(filledDaily.length / 2)) && (
                  <text x={xCenter} y={height - 12} textAnchor="middle" fontSize="10" fill="#6b7280">
                    {formatDateKey(day.dateKey)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        <ChartTooltip x={hover?.x || 0} y={hover?.y || 0} visible={!!hover}>
          {hover && (
            <div className="space-y-0.5">
              <div className="text-[10px] uppercase tracking-wider text-gray-400">
                {formatDateKey(hover.day.dateKey)} · total {formatNumber(hover.day.total)} {unitLabel}
              </div>
              <div className="flex items-center gap-1.5">
                <span
                  className="inline-block w-2 h-2 rounded-sm"
                  style={{ background: hover.seg.name === 'Other' ? OTHER_COLOR : colorByModel.get(hover.seg.name) }}
                />
                <span className="font-semibold">{hover.seg.name}</span>
                <span className="text-gray-300">·</span>
                <span>{formatNumber(hover.seg.count)} {unitLabel}</span>
              </div>
            </div>
          )}
        </ChartTooltip>
      </div>
      <Legend items={legendItems} />
    </div>
  );
};

const ModelMixDonut = ({ models }) => {
  const [hoverModel, setHoverModel] = useState(null);

  const ordered = models.slice(0, STACK_MAX_MODELS);
  const rest = models.slice(STACK_MAX_MODELS).reduce((s, m) => s + (m.total || 0), 0);
  const slices = rest > 0
    ? [...ordered, { model: 'Other', total: rest }]
    : ordered;
  const total = slices.reduce((s, m) => s + (m.total || 0), 0);

  if (total === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">Model Mix</h2>
        <p className="mt-3 text-sm text-gray-500">No model usage for this selection.</p>
      </div>
    );
  }

  const cx = 90;
  const cy = 90;
  const outerR = 80;
  const hoverOuterR = 86;
  const innerR = 52;
  let cursor = 0;
  const arcs = slices.map((slice, index) => {
    const startAngle = (cursor / total) * 360;
    cursor += slice.total;
    const endAngle = (cursor / total) * 360;
    const fill = slice.model === 'Other' ? OTHER_COLOR : MODEL_COLORS[index % MODEL_COLORS.length];
    const safeEnd = Math.min(endAngle, startAngle + 359.999);
    const d = endAngle - startAngle > 0.0001 ? describeArc(cx, cy, innerR, outerR, startAngle, safeEnd) : null;
    const dHover = endAngle - startAngle > 0.0001 ? describeArc(cx, cy, innerR, hoverOuterR, startAngle, safeEnd) : null;
    return { ...slice, d, dHover, fill, pct: (slice.total / total) * 100 };
  });

  const activeArc = hoverModel ? arcs.find((a) => a.model === hoverModel) : null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <h2 className="text-sm font-semibold text-gray-900">Model Mix</h2>
      <div className="mt-3 flex items-center gap-4">
        <svg viewBox="0 0 180 180" className="w-[180px] h-[180px] shrink-0">
          {arcs.map((arc) => arc.d && (
            <path
              key={arc.model}
              d={hoverModel === arc.model ? arc.dHover : arc.d}
              fill={arc.fill}
              fillOpacity={hoverModel && hoverModel !== arc.model ? 0.4 : 1}
              onMouseEnter={() => setHoverModel(arc.model)}
              onMouseLeave={() => setHoverModel(null)}
              style={{ cursor: 'pointer', transition: 'd 120ms ease-out, fill-opacity 120ms' }}
            />
          ))}
          {activeArc ? (
            <>
              <text x={cx} y={cy - 6} textAnchor="middle" fontSize="10" fill="#6b7280">
                {activeArc.pct.toFixed(1)}%
              </text>
              <text x={cx} y={cy + 10} textAnchor="middle" fontSize="13" fontWeight="600" fill="#111827">
                {formatNumber(activeArc.total)}
              </text>
              <text x={cx} y={cy + 24} textAnchor="middle" fontSize="9" fill="#6b7280">
                <tspan>{activeArc.model.length > 18 ? `${activeArc.model.slice(0, 17)}…` : activeArc.model}</tspan>
              </text>
            </>
          ) : (
            <>
              <text x={cx} y={cy - 4} textAnchor="middle" fontSize="11" fill="#6b7280">Total</text>
              <text x={cx} y={cy + 14} textAnchor="middle" fontSize="14" fontWeight="600" fill="#111827">
                {formatNumber(total)}
              </text>
            </>
          )}
        </svg>
        <div className="flex-1 min-w-0 space-y-1">
          {arcs.map((arc) => {
            const dim = hoverModel && hoverModel !== arc.model;
            return (
              <button
                key={arc.model}
                type="button"
                onMouseEnter={() => setHoverModel(arc.model)}
                onMouseLeave={() => setHoverModel(null)}
                className={`w-full flex items-center justify-between gap-2 text-xs px-1.5 py-1 rounded transition-colors ${
                  hoverModel === arc.model ? 'bg-gray-100' : 'hover:bg-gray-50'
                } ${dim ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: arc.fill }} />
                  <span className="truncate text-gray-700">{arc.model}</span>
                </div>
                <span className="text-gray-600 tabular-nums">{arc.pct.toFixed(1)}%</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const HorizontalBarChart = ({ title, items, emptyLabel, valueKey = 'total', labelKey = 'model', color = '#2563eb' }) => {
  const maxValue = items.reduce((max, item) => Math.max(max, item[valueKey] || 0), 0);
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">{emptyLabel}</p>
      ) : (
        <div className="mt-4 space-y-3">
          {items.map((item, index) => {
            const value = item[valueKey] || 0;
            const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
            return (
              <div
                key={`${item[labelKey]}-${index}`}
                className="group space-y-1 rounded-md px-1 -mx-1 transition-colors hover:bg-gray-50"
                title={`${item[labelKey]}: ${formatNumber(value)}`}
              >
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate font-medium text-gray-700 group-hover:text-gray-900">{item[labelKey]}</span>
                  <span className="text-gray-600 tabular-nums group-hover:text-gray-900">{formatNumber(value)}</span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-100">
                  <div
                    className="h-2 rounded-full transition-all duration-300"
                    style={{ width: `${pct}%`, background: color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------- filter controls ----------

const ModelFilterChips = ({ availableModels, selectedModels, onToggle, onClear }) => {
  if (!availableModels.length) return null;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-medium uppercase tracking-wider text-gray-500">Models</label>
        {selectedModels.size > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-blue-600 hover:underline"
          >
            Clear
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {availableModels.map((model) => {
          const active = selectedModels.has(model);
          return (
            <button
              key={model}
              type="button"
              onClick={() => onToggle(model)}
              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                active
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
              }`}
            >
              {model}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const UserFilter = ({ users, selectedUserId, onChange }) => (
  <div>
    <label className="text-xs font-medium uppercase tracking-wider text-gray-500">User</label>
    <select
      value={selectedUserId || ''}
      onChange={(e) => onChange(e.target.value || null)}
      className="mt-2 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
    >
      <option value="">All users</option>
      {users.map((u) => (
        <option key={u.userId} value={u.userId}>
          {u.displayName || u.email || u.userId}
          {u.email && u.displayName ? ` (${u.email})` : ''}
        </option>
      ))}
    </select>
  </div>
);

// ---------- page ----------

const AdminDashboardPage = () => {
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const exportStatusTimeoutRef = useRef(null);
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exportingFormat, setExportingFormat] = useState(null);
  const [exportStatus, setExportStatus] = useState(null);
  const [error, setError] = useState(null);
  const [usageMetric, setUsageMetric] = useState('tokens');
  const [selectedModels, setSelectedModels] = useState(() => new Set());
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [dashboard, setDashboard] = useState({
    summary: { overallTotal: 0, totalModels: 0, totalUsers: 0 },
    daily: [],
    topModels: [],
    users: [],
    range: { startDate: defaultRange.startDate, endDate: defaultRange.endDate, dayCount: 30 },
  });

  const loadDashboard = useCallback(async ({ startDate: queryStart, endDate: queryEnd, userId, metric = 'tokens' } = {}) => {
    const resolvedStart = queryStart || defaultRange.startDate;
    const resolvedEnd = queryEnd || defaultRange.endDate;
    setError(null);
    setExportStatus(null);
    setRefreshing(true);
    try {
      const data = await fetchAdminDashboardUsage({
        startDate: resolvedStart,
        endDate: resolvedEnd,
        topUsers: 200,
        topModels: 50,
        userId: userId || null,
        metric,
      });
      setDashboard((prev) => {
        // Preserve prior users roster when filtering by user so the dropdown stays populated.
        const nextUsers = userId && prev.users && prev.users.length > data.users.length
          ? prev.users
          : data.users;
        return { ...data, users: nextUsers };
      });
      setStartDate(data.range?.startDate || resolvedStart);
      setEndDate(data.range?.endDate || resolvedEnd);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard data.');
    } finally {
      setRefreshing(false);
    }
  }, [defaultRange.endDate, defaultRange.startDate]);

  useEffect(() => () => {
    if (exportStatusTimeoutRef.current) {
      window.clearTimeout(exportStatusTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    const checkAdminAndLoad = async () => {
      const user = auth.currentUser;
      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      const tokenResult = await user.getIdTokenResult();
      const adminClaim = tokenResult?.claims?.admin === true;
      setIsAdmin(adminClaim);

      if (adminClaim) {
        await loadDashboard({
          startDate: defaultRange.startDate,
          endDate: defaultRange.endDate,
          metric: 'tokens',
        });
      }
      setLoading(false);
    };

    checkAdminAndLoad().catch((err) => {
      setError(err.message || 'Failed to initialize admin dashboard.');
      setLoading(false);
    });
  }, [defaultRange.endDate, defaultRange.startDate, loadDashboard]);

  const handleApplyRange = () => {
    loadDashboard({ startDate, endDate, userId: selectedUserId, metric: usageMetric });
  };

  const handlePreset = (days) => {
    const today = new Date();
    const nextEnd = getDateKeyFromDate(today);
    const nextStart = getDateKeyFromDate(shiftDate(today, -(days - 1)));
    setStartDate(nextStart);
    setEndDate(nextEnd);
    loadDashboard({
      startDate: nextStart,
      endDate: nextEnd,
      userId: selectedUserId,
      metric: usageMetric,
    });
  };

  const handleUserChange = (nextUserId) => {
    setSelectedUserId(nextUserId);
    loadDashboard({ startDate, endDate, userId: nextUserId, metric: usageMetric });
  };

  const handleUsageMetricChange = (nextMetric) => {
    if (nextMetric === usageMetric) return;
    setUsageMetric(nextMetric);
    loadDashboard({
      startDate,
      endDate,
      userId: selectedUserId,
      metric: nextMetric,
    });
  };

  const handleExportUsage = async (format) => {
    if (exportingFormat) return;

    setError(null);
    setExportStatus(null);
    setExportingFormat(format);

    try {
      const selectedModelList = Array.from(selectedModels).sort();
      const { blob, filename } = await fetchAdminDashboardUsageExport({
        startDate,
        endDate,
        userId: selectedUserId,
        models: selectedModelList,
        format,
      });
      const fallbackFilename = buildExportFilename({
        startDate,
        endDate,
        userId: selectedUserId,
        format,
      });

      downloadBlob(blob, filename || fallbackFilename);
      setExportStatus(`Token usage ${format.toUpperCase()} downloaded.`);

      if (exportStatusTimeoutRef.current) {
        window.clearTimeout(exportStatusTimeoutRef.current);
      }
      exportStatusTimeoutRef.current = window.setTimeout(() => {
        setExportStatus(null);
        exportStatusTimeoutRef.current = null;
      }, EXPORT_STATUS_TIMEOUT_MS);
    } catch (err) {
      setError(err.message || 'Failed to export token usage.');
    } finally {
      setExportingFormat(null);
    }
  };

  const toggleModel = (model) => {
    setSelectedModels((prev) => {
      const next = new Set(prev);
      if (next.has(model)) next.delete(model);
      else next.add(model);
      return next;
    });
  };

  const clearModels = () => setSelectedModels(new Set());

  // --- derived data (applies model filter) ---

  const allModelNames = useMemo(
    () => (dashboard.topModels || []).map((m) => m.model),
    [dashboard.topModels]
  );

  const modelFilterActive = selectedModels.size > 0;
  const userFilterActive = Boolean(selectedUserId);

  const filteredDaily = useMemo(() => {
    return (dashboard.daily || []).map((day) => {
      if (!modelFilterActive) return day;
      const models = (day.models || []).filter((m) => selectedModels.has(m.model));
      const total = models.reduce((s, m) => s + (m.count || 0), 0);
      return { ...day, total, models };
    });
  }, [dashboard.daily, modelFilterActive, selectedModels]);

  const filteredTopModels = useMemo(() => {
    if (!modelFilterActive) return dashboard.topModels || [];
    return (dashboard.topModels || []).filter((m) => selectedModels.has(m.model));
  }, [dashboard.topModels, modelFilterActive, selectedModels]);

  const filteredUsers = useMemo(() => {
    return (dashboard.users || [])
      .map((user) => {
        const breakdown = modelFilterActive
          ? (user.modelBreakdown || []).filter((m) => selectedModels.has(m.model))
          : user.modelBreakdown || [];
        const total = modelFilterActive
          ? breakdown.reduce((s, m) => s + (m.count || 0), 0)
          : user.total;
        return { ...user, total, modelBreakdown: breakdown };
      })
      .filter((user) => user.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [dashboard.users, modelFilterActive, selectedModels]);

  const selectedUser = useMemo(
    () => (userFilterActive ? filteredUsers.find((u) => u.userId === selectedUserId) : null),
    [filteredUsers, selectedUserId, userFilterActive]
  );

  const filteredTotal = useMemo(
    () => filteredDaily.reduce((s, d) => s + (d.total || 0), 0),
    [filteredDaily]
  );

  if (!auth.currentUser) {
    return (
      <AdminLayout>
        <div className="p-6 text-red-600">Sign in to access admin tools.</div>
      </AdminLayout>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 text-gray-600">Loading dashboard...</div>
      </AdminLayout>
    );
  }

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="p-6 text-red-600">You need admin access to view this dashboard.</div>
      </AdminLayout>
    );
  }

  const summary = dashboard.summary || {};
  const range = dashboard.range || {};
  const metricConfig = getUsageMetricConfig(usageMetric);

  return (
    <AdminLayout>
      <header className="sticky top-0 z-10 shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Usage Dashboard</h1>
            <p className="text-xs text-gray-500">
              {range.startDate} → {range.endDate} · {range.dayCount || 0} days · {metricConfig.label}
              {modelFilterActive && ` · ${selectedModels.size} model${selectedModels.size === 1 ? '' : 's'} filtered`}
              {userFilterActive && selectedUser && ` · user: ${selectedUser.displayName || selectedUser.email || selectedUser.userId}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => handleExportUsage('csv')}
              disabled={refreshing || Boolean(exportingFormat)}
              title="Download token usage CSV"
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              {exportingFormat === 'csv' ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Download size={15} />
              )}
              <span>CSV</span>
            </button>
            <button
              type="button"
              onClick={() => handleExportUsage('json')}
              disabled={refreshing || Boolean(exportingFormat)}
              title="Download token usage JSON"
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              {exportingFormat === 'json' ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <FileJson size={15} />
              )}
              <span>JSON</span>
            </button>
            <button
              type="button"
              onClick={() => loadDashboard({ startDate, endDate, userId: selectedUserId, metric: usageMetric })}
              disabled={refreshing || Boolean(exportingFormat)}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-blue-600 px-3 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {refreshing ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <RefreshCw size={15} />
              )}
              <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>
        </header>

        <main className="flex-1 min-h-0 overflow-auto px-6 py-6 space-y-6">
          {/* Filters */}
          <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="grid gap-4 lg:grid-cols-[auto_180px_1fr_260px]">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <label className="text-xs font-medium uppercase tracking-wider text-gray-500">
                  <span className="block mb-2">Start</span>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="block rounded-md border border-gray-300 px-3 py-2 text-sm font-normal normal-case tracking-normal text-gray-900"
                  />
                </label>
                <label className="text-xs font-medium uppercase tracking-wider text-gray-500">
                  <span className="block mb-2">End</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="block rounded-md border border-gray-300 px-3 py-2 text-sm font-normal normal-case tracking-normal text-gray-900"
                  />
                </label>
                <button
                  type="button"
                  onClick={handleApplyRange}
                  disabled={refreshing}
                  className="h-10 rounded-md bg-gray-900 px-4 text-sm text-white hover:bg-black disabled:opacity-60"
                >
                  Apply
                </button>
                <div className="flex items-end gap-1">
                  {[7, 30, 90].map((days) => (
                    <button
                      key={days}
                      type="button"
                      onClick={() => handlePreset(days)}
                      className="rounded-md border border-gray-300 bg-white px-2.5 py-2 text-xs text-gray-700 hover:bg-gray-100"
                    >
                      {days}d
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-gray-500">Metric</label>
                <div className="mt-2 inline-flex rounded-md border border-gray-300 bg-white p-1">
                  {Object.entries(USAGE_METRIC_CONFIG).map(([metric, config]) => {
                    const active = usageMetric === metric;
                    return (
                      <button
                        key={metric}
                        type="button"
                        onClick={() => handleUsageMetricChange(metric)}
                        className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                          active
                            ? 'bg-gray-900 text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {config.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <ModelFilterChips
                availableModels={allModelNames}
                selectedModels={selectedModels}
                onToggle={toggleModel}
                onClear={clearModels}
              />

              <UserFilter
                users={dashboard.users || []}
                selectedUserId={selectedUserId}
                onChange={handleUserChange}
              />
            </div>
          </section>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {exportStatus && (
            <div className="inline-flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
              <Check size={16} />
              <span>{exportStatus}</span>
            </div>
          )}

          {/* Stat cards */}
          <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              label={modelFilterActive ? metricConfig.filteredTotalLabel : metricConfig.totalLabel}
              value={modelFilterActive ? filteredTotal : summary.overallTotal}
              hint={modelFilterActive ? `across ${selectedModels.size} model${selectedModels.size === 1 ? '' : 's'}` : 'across all models'}
            />
            <StatCard
              label={userFilterActive ? 'Selected User' : 'Unique Users'}
              value={userFilterActive ? 1 : summary.totalUsers}
              hint={userFilterActive
                ? (selectedUser?.displayName || selectedUser?.email || selectedUserId || 'user')
                : `${filteredUsers.length} in view`}
            />
            <StatCard
              label="Models Used"
              value={modelFilterActive ? selectedModels.size : summary.totalModels}
              hint={modelFilterActive ? 'selected' : 'in range'}
            />
            <StatCard
              label="Days in Range"
              value={range.dayCount || 0}
              hint={`${formatDateKey(range.startDate || startDate)} – ${formatDateKey(range.endDate || endDate)}`}
            />
          </section>

          {/* Primary trend */}
          <DailyUsageChart
            daily={filteredDaily}
            label={modelFilterActive ? metricConfig.filteredDailyLabel : metricConfig.dailyLabel}
            unitLabel={metricConfig.unitPlural}
          />

          {/* Stacked + donut */}
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <StackedBarChart
                daily={filteredDaily}
                models={filteredTopModels}
                unitLabel={metricConfig.unitPlural}
              />
            </div>
            <ModelMixDonut models={filteredTopModels} />
          </section>

          {/* User summary (when filtering by user) OR Top Models + Top Users */}
          {userFilterActive ? (
            <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-gray-900 truncate">
                    {selectedUser?.displayName || selectedUser?.email || selectedUserId}
                  </h2>
                  {selectedUser?.email && selectedUser?.displayName && (
                    <p className="text-xs text-gray-500 truncate">{selectedUser.email}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {formatNumber(filteredTotal)} {metricConfig.unitPlural} in range
                    {modelFilterActive ? ` · ${selectedModels.size} selected model${selectedModels.size === 1 ? '' : 's'}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleUserChange(null)}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Clear user
                </button>
              </div>
              <div className="mt-4">
                <HorizontalBarChart
                  title="Model Breakdown"
                  items={filteredTopModels}
                  emptyLabel="No model activity for this user in the selection."
                  labelKey="model"
                  valueKey="total"
                  color="#9333ea"
                />
              </div>
            </section>
          ) : (
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <HorizontalBarChart
                title="Top Models"
                items={filteredTopModels}
                emptyLabel="No model usage found for this range."
                labelKey="model"
                valueKey="total"
                color="#2563eb"
              />
              <HorizontalBarChart
                title="Top Users"
                items={filteredUsers.slice(0, 10).map((user) => ({
                  user: user.displayName || user.email || user.userId,
                  total: user.total,
                }))}
                emptyLabel="No user usage found for this range."
                labelKey="user"
                valueKey="total"
                color="#0d9488"
              />
            </section>
          )}

          {/* Tables */}
          <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {!userFilterActive && (
              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm overflow-auto">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-900">Usage By User</h2>
                  <span className="text-xs text-gray-500">{filteredUsers.length} shown</span>
                </div>
                <table className="mt-3 min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="pb-2 pr-3 font-medium">User</th>
                      <th className="pb-2 pr-3 font-medium tabular-nums">Total</th>
                      <th className="pb-2 font-medium">Top Models</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.slice(0, 30).map((user) => (
                      <tr key={user.userId} className="border-b last:border-b-0 hover:bg-gray-50">
                        <td className="py-2 pr-3">
                          <button
                            type="button"
                            onClick={() => handleUserChange(user.userId)}
                            className="text-left"
                          >
                            <div className="font-medium text-gray-800 hover:text-blue-700">
                              {user.displayName || user.email || user.userId}
                            </div>
                            {user.email && user.displayName && (
                              <div className="text-xs text-gray-500">{user.email}</div>
                            )}
                          </button>
                        </td>
                        <td className="py-2 pr-3 text-gray-800 tabular-nums">{formatNumber(user.total)}</td>
                        <td className="py-2 text-gray-700">
                          {(user.modelBreakdown || []).slice(0, 3).map((entry) => (
                            <div key={`${user.userId}-${entry.model}`} className="text-xs">
                              {entry.model}: {formatNumber(entry.count)}
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 && (
                      <tr>
                        <td colSpan={3} className="py-6 text-center text-sm text-gray-500">
                          No users match the current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm overflow-auto">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">Usage By Date</h2>
                <span className="text-xs text-gray-500">{filteredDaily.length} days</span>
              </div>
              <table className="mt-3 min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="pb-2 pr-3 font-medium">Date</th>
                    <th className="pb-2 pr-3 font-medium tabular-nums">Total</th>
                    <th className="pb-2 font-medium">Top Models</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDaily.map((day) => (
                    <tr key={day.dateKey} className="border-b last:border-b-0">
                      <td className="py-2 pr-3 text-gray-800">{day.dateKey}</td>
                      <td className="py-2 pr-3 text-gray-800 tabular-nums">{formatNumber(day.total)}</td>
                      <td className="py-2 text-gray-700">
                        {(day.models || []).slice(0, 3).map((entry) => (
                          <div key={`${day.dateKey}-${entry.model}`} className="text-xs">
                            {entry.model}: {formatNumber(entry.count)}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
      </main>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
